'use client';

import { faArrowUp, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

interface Props {
  onClick: () => void;
  disabled?: boolean;
  isSubmitting?: boolean;
  variant?: 'send' | 'next';
  /** Mobile UT Task question: disable + relabel until the user closes the task bar. */
  awaitingTaskCompletion?: boolean;
}

/** Matches ProjectResponseSelectSendButton styling for visual parity with UT chat. */
export default function HandsFreePopupSendButton({
  onClick,
  disabled,
  isSubmitting = false,
  variant = 'send',
  awaitingTaskCompletion = false,
}: Props) {
  const t = useTranslations('Chat');

  const isNext = variant === 'next';

  const label = (() => {
    // Hardcoded copy (CU-868kjtjep) — intentionally not an i18n key.
    if (awaitingTaskCompletion) return 'Complete task, then continue';
    return isNext ? t('next') : t('send');
  })();

  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled || awaitingTaskCompletion}
      className='flex h-8 w-full items-center justify-center gap-2 rounded-full bg-(--brand-primary) px-3 py-2 transition-all disabled:opacity-20'
      data-test='handsfree-popup-send-button'
    >
      {isSubmitting ? (
        <FontAwesomeIcon
          icon={faSpinner}
          className='relative size-6 animate-spin text-(--brand-primary-contrast)'
        />
      ) : (
        <div className='flex flex-nowrap items-center justify-center gap-2 text-center'>
          {!isNext && (
            <FontAwesomeIcon
              icon={faArrowUp}
              className='relative h-3 w-2.5 text-(--brand-primary-contrast)'
            />
          )}
          <div className='h-4'>
            <span className='block text-center [font-family:var(--brand-font)] text-xs font-medium text-(--brand-primary-contrast)'>
              {label}
            </span>
          </div>
        </div>
      )}
    </button>
  );
}
